//src/components/vcard/LuggageInfo.tsx
import React, { useState } from "react";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import type { VCard, MessagingContact } from "../../types/vcard";
import OwnerSection from "./shared/OwnerSection";
import { useVCardProfileSync } from "../../hooks/useVCardProfileSync";
import useUniversalToggleWithAddress from "../../hooks/useUniversalToggleWithAddress";
import { DEFAULT_COUNTRY_CODE } from "../../constants/countryCodes";
import {
  formatPhoneBodyForDisplay,
  normalizePhoneForStorage,
  stripCountryCodeForDisplay,
} from "../../utils/phone";
import CountryCodeSelect from "../ui/CountryCodeSelect";
import AddressAutocompleteInput from "../ui/AddressAutocompleteInput";
import { useUserStore } from "../../store/userStore";
import type { UserProfile } from "../../types/user";
import type { ResolvedAddress } from "../../services/address/googlePlaces";

const getLuggageCategoryInfo = (
  categorySpecificInfo: VCard["categorySpecificInfo"] | undefined,
): Record<string, unknown> => {
  if (!categorySpecificInfo || typeof categorySpecificInfo !== "object") {
    return {};
  }

  const luggageInfo = (categorySpecificInfo as Record<string, unknown>).luggage;
  if (!luggageInfo || typeof luggageInfo !== "object") {
    return {};
  }

  return luggageInfo as Record<string, unknown>;
};

const getCountryCode = (value: unknown, fallback: string): string =>
  typeof value === "string" && value.trim().length > 0 ? value : fallback;

interface LuggageInfoProps {
  formData: Partial<VCard>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isSubmitting?: boolean;
  showSaveConfirmation?: boolean;
}

const LuggageInfo: React.FC<LuggageInfoProps> = ({
  formData,
  handleChange,
  handleSubmit,
  isSubmitting = false,
  showSaveConfirmation = false,
}) => {
  const [mobilePhoneCountryCode, setMobilePhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [workPhoneCountryCode, setWorkPhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [travelPhoneCountryCode, setTravelPhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [hotelCountryCode, setHotelCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [localShowSaveConfirmation, setLocalShowSaveConfirmation] =
    useState(false);
  const profile = useUserStore((state) => state.profile);
  const updateProfile = useUserStore((state) => state.updateProfile);

  // Use the universal profile sync hook
  const { vcard: luggageVCard, updateField } = useVCardProfileSync("luggage", {
    syncFields: [
      "firstName",
      "lastName",
      "email",
      "mobilePhone",
      "workEmail",
      "workPhone",
      "travelPhone",
      "messagingContact",
      "messagingContacts",
      "hotelInfo",
      "isActive",
    ],
    debounceMs: 300,
  });

  // Initialize our universal toggle hook for address update
  const {
    universalToggleWithAddress,
    isLoading: toggleLoading,
    error: toggleError,
  } = useUniversalToggleWithAddress();

  // Handler for toggling luggage active state
  const handleToggle = async () => {
    try {
      await universalToggleWithAddress(luggageVCard?.id || "luggage");
    } catch (err) {
      console.error(err);
    }
  };

  // Use synced data if available
  const luggageVCardRecord = (luggageVCard ?? {}) as Record<string, unknown>;
  const syncedMobilePhoneCountryCode = getCountryCode(
    luggageVCardRecord.mobilePhoneCountryCode,
    mobilePhoneCountryCode,
  );
  const syncedWorkPhoneCountryCode = getCountryCode(
    luggageVCardRecord.workPhoneCountryCode,
    workPhoneCountryCode,
  );
  const syncedTravelPhoneCountryCode = getCountryCode(
    luggageVCardRecord.travelPhoneCountryCode,
    travelPhoneCountryCode,
  );

  const syncedFormData = {
    ...formData,
    firstName: luggageVCard?.firstName || formData.firstName,
    lastName: luggageVCard?.lastName || formData.lastName,
    email:
      luggageVCard?.email ||
      luggageVCard?.personalEmail ||
      formData.email ||
      formData.personalEmail,
    phone: luggageVCard?.phone || formData.phone,
    workEmail: luggageVCard?.workEmail || formData.workEmail,
    mobilePhone: luggageVCard?.mobilePhone || formData.mobilePhone,
    mobilePhoneCountryCode: syncedMobilePhoneCountryCode,
    workPhone: luggageVCard?.workPhone || formData.workPhone,
    workPhoneCountryCode: syncedWorkPhoneCountryCode,
    travelPhone: luggageVCard?.travelPhone || formData.travelPhone,
    travelPhoneCountryCode: syncedTravelPhoneCountryCode,
    messagingContacts: Array.isArray(formData.messagingContacts)
      ? formData.messagingContacts
      : Array.isArray(luggageVCard?.messagingContacts)
        ? luggageVCard.messagingContacts
        : Array.isArray(profile?.messagingContacts)
          ? profile.messagingContacts
          : [],
    messagingContact:
      formData.messagingContact ||
      luggageVCard?.messagingContact ||
      profile?.messagingContact ||
      formData.messagingContacts?.[0] ||
      luggageVCard?.messagingContacts?.[0] ||
      profile?.messagingContacts?.[0],
    hotelInfo: luggageVCard?.hotelInfo || formData.hotelInfo,
    categorySpecificInfo: {
      ...formData.categorySpecificInfo,
      luggage: {
        ...getLuggageCategoryInfo(formData.categorySpecificInfo),
        ...getLuggageCategoryInfo(luggageVCard?.categorySpecificInfo),
      },
    },
    isActive: luggageVCard?.isActive || formData.isActive || false,
  };
  const syncedLuggageCategoryInfo = getLuggageCategoryInfo(
    syncedFormData.categorySpecificInfo,
  );
  const travelInsuranceInfo =
    syncedLuggageCategoryInfo.travelInsurance &&
    typeof syncedLuggageCategoryInfo.travelInsurance === "object"
      ? (syncedLuggageCategoryInfo.travelInsurance as Record<string, unknown>)
      : {};

  const emitFieldChange = (name: string, value: string): void => {
    handleChange({
      target: {
        name,
        value,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleResolvedHotelAddress = (
    resolvedAddress: ResolvedAddress,
  ): void => {
    const updates: Array<{ name: string; value: string }> = [
      {
        name: "hotelInfo.address.line1",
        value: resolvedAddress.line1,
      },
      {
        name: "hotelInfo.address.city",
        value: resolvedAddress.city,
      },
      {
        name: "hotelInfo.address.state",
        value: resolvedAddress.state,
      },
      {
        name: "hotelInfo.address.zip",
        value: resolvedAddress.postalCode,
      },
      {
        name: "hotelInfo.address.country",
        value: resolvedAddress.country,
      },
    ];

    updates.forEach((update) => {
      if (update.value.trim()) {
        emitFieldChange(update.name, update.value);
      }
    });
  };

  // Enhanced submit handler with proper sync
  const handleEnhancedSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formDataSnapshot = new FormData(e.currentTarget as HTMLFormElement);
    try {
      // First handle the regular submit
      await handleSubmit(e);

      // Sync luggage-specific data
      const luggageCategoryInfo = getLuggageCategoryInfo(
        formData.categorySpecificInfo,
      );
      if (luggageVCard && Object.keys(luggageCategoryInfo).length > 0) {
        await updateField(
          "categorySpecificInfo.luggage",
          luggageCategoryInfo,
          "profile",
        );
      }

      // Show save confirmation
      setLocalShowSaveConfirmation(true);
      setTimeout(() => setLocalShowSaveConfirmation(false), 3000);

      if (profile) {
        const profileUpdates: Partial<UserProfile> = {};
        const formFirstName =
          (formDataSnapshot.get("firstName") as string) || "";
        const formLastName = (formDataSnapshot.get("lastName") as string) || "";
        const formEmail = (formDataSnapshot.get("email") as string) || "";
        const formWorkEmail =
          (formDataSnapshot.get("workEmail") as string) || "";
        const formMobilePhone =
          (formDataSnapshot.get("mobilePhone") as string) || "";
        const formWorkPhone =
          (formDataSnapshot.get("workPhone") as string) || "";
        const formTravelPhone =
          (formDataSnapshot.get("travelPhone") as string) || "";
        const normalizeMessagingContacts = (
          contacts: Array<MessagingContact | null | undefined>,
        ): MessagingContact[] =>
          contacts
            .filter((contact): contact is MessagingContact =>
              Boolean(contact && contact.app),
            )
            .map((contact) => ({
              app: contact.app,
              handle: contact.handle || "",
            }));

        if (formFirstName && formFirstName !== profile.firstName) {
          profileUpdates.firstName = formFirstName;
        }

        if (formLastName && formLastName !== profile.lastName) {
          profileUpdates.lastName = formLastName;
        }

        if (formEmail && formEmail !== profile.email) {
          profileUpdates.email = formEmail;
        }

        if (formWorkEmail !== (profile.alternativeEmail || "")) {
          profileUpdates.alternativeEmail = formWorkEmail;
        }

        if (formMobilePhone && formMobilePhone !== profile.phone) {
          profileUpdates.phone = formMobilePhone;
        }

        if (formWorkPhone !== (profile.workPhone || "")) {
          profileUpdates.workPhone = formWorkPhone;
        }

        if (formTravelPhone !== (profile.travelPhone || "")) {
          profileUpdates.travelPhone = formTravelPhone;
        }

        const submittedMessagingContacts = normalizeMessagingContacts(
          Array.isArray(formData.messagingContacts)
            ? formData.messagingContacts
            : formData.messagingContact
              ? [formData.messagingContact]
              : [],
        );
        const currentMessagingContacts = normalizeMessagingContacts(
          Array.isArray(profile.messagingContacts)
            ? profile.messagingContacts
            : profile.messagingContact
              ? [profile.messagingContact]
              : [],
        );
        const submittedPrimaryMessagingContact = formData.messagingContact?.app
          ? {
              app: formData.messagingContact.app,
              handle: formData.messagingContact.handle || "",
            }
          : submittedMessagingContacts[0] || null;
        const currentPrimaryMessagingContact = profile.messagingContact?.app
          ? {
              app: profile.messagingContact.app,
              handle: profile.messagingContact.handle || "",
            }
          : currentMessagingContacts[0] || null;
        if (
          JSON.stringify(submittedMessagingContacts) !==
            JSON.stringify(currentMessagingContacts) ||
          JSON.stringify(submittedPrimaryMessagingContact) !==
            JSON.stringify(currentPrimaryMessagingContact)
        ) {
          profileUpdates.messagingContacts = submittedMessagingContacts;
          profileUpdates.messagingContact = submittedPrimaryMessagingContact;
        }

        if (Object.keys(profileUpdates).length > 0) {
          await updateProfile(profileUpdates);
        }
      }
    } catch (error) {
      console.error("Failed to sync data:", error);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        void handleEnhancedSubmit(e);
      }}
      className="space-y-6"
    >
      {(showSaveConfirmation || localShowSaveConfirmation) && (
        <div className="flex items-center space-x-2 p-4 bg-green-50 text-green-700 rounded-lg">
          <CheckCircle2 className="h-5 w-5" />
          <span>Changes saved successfully!</span>
        </div>
      )}

      {/* Active/Inactive Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Luggage Tag
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            (Defaults to Home when not Active)
          </span>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={syncedFormData.isActive}
            onChange={() => {
              void handleToggle();
            }}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
          <span className="ml-2 text-sm font-medium text-gray-900">
            {syncedFormData.isActive ? (
              <span className="text-green-600">Active</span>
            ) : (
              <span className="text-orange-500">Inactive</span>
            )}
          </span>
        </label>
      </div>

      {/* Owner Information */}
      <OwnerSection
        formData={syncedFormData}
        handleChange={handleChange}
        mobilePhoneCountryCode={syncedFormData.mobilePhoneCountryCode}
        setMobilePhoneCountryCode={setMobilePhoneCountryCode}
        workPhoneCountryCode={syncedFormData.workPhoneCountryCode}
        setWorkPhoneCountryCode={setWorkPhoneCountryCode}
        travelPhoneCountryCode={syncedFormData.travelPhoneCountryCode}
        setTravelPhoneCountryCode={setTravelPhoneCountryCode}
      />

      {/* Hotel Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Accommodation Information
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hotel Name
            </label>
            <input
              type="text"
              name="hotelInfo.name"
              value={syncedFormData.hotelInfo?.name || ""}
              onChange={handleChange}
              className="w-full md:max-w-[50%] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Street Address
            </label>
            <AddressAutocompleteInput
              value={syncedFormData.hotelInfo?.address?.line1 || ""}
              onChange={(nextValue) => {
                emitFieldChange("hotelInfo.address.line1", nextValue);
              }}
              onResolvedAddress={handleResolvedHotelAddress}
              inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Start typing hotel address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Room Number (Optional)
            </label>
            <input
              type="text"
              name="hotelInfo.address.line2"
              value={syncedFormData.hotelInfo?.address?.line2 || ""}
              onChange={handleChange}
              className="w-full sm:max-w-[220px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                City
              </label>
              <input
                type="text"
                name="hotelInfo.address.city"
                value={syncedFormData.hotelInfo?.address?.city || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                State/Province
              </label>
              <input
                type="text"
                name="hotelInfo.address.state"
                value={syncedFormData.hotelInfo?.address?.state || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                ZIP/Postal Code
              </label>
              <input
                type="text"
                name="hotelInfo.address.zip"
                value={syncedFormData.hotelInfo?.address?.zip || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Country
              </label>
              <input
                type="text"
                name="hotelInfo.address.country"
                value={syncedFormData.hotelInfo?.address?.country || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Check-in Date
              </label>
              <input
                type="date"
                name="hotelInfo.checkIn"
                value={syncedFormData.hotelInfo?.checkIn || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Check-out Date
              </label>
              <input
                type="date"
                name="hotelInfo.checkOut"
                value={syncedFormData.hotelInfo?.checkOut || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Hotel Phone
              </label>
              <div className="flex w-full space-x-2">
                <CountryCodeSelect
                  value={hotelCountryCode}
                  onChange={setHotelCountryCode}
                  className="w-24"
                  buttonClassName="h-[41px]"
                />
                <input
                  type="tel"
                  name="hotelInfo.phone"
                  value={formatPhoneBodyForDisplay(
                    stripCountryCodeForDisplay(
                      syncedFormData.hotelInfo?.phone,
                      hotelCountryCode,
                    ),
                  )}
                  onChange={(e) => {
                    handleChange({
                      target: {
                        name: "hotelInfo.phone",
                        value: normalizePhoneForStorage(
                          e.target.value,
                          hotelCountryCode,
                        ),
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  minLength={8}
                  maxLength={15}
                  size={15}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Hotel Email
              </label>
              <input
                type="email"
                name="hotelInfo.email"
                value={syncedFormData.hotelInfo?.email || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Travel Insurance */}
      <div>
        <div className="rounded-lg border border-red-300 p-4 dark:border-red-500/60">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-900 dark:text-gray-100">
            <ShieldAlert className="h-5 w-5 text-red-600" />
            <span>Travel Insurance</span>
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[0.85fr_1.15fr]">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Insurer Name
                </label>
                <input
                  type="text"
                  name="categorySpecificInfo.luggage.travelInsurance.insurerName"
                  value={
                    typeof travelInsuranceInfo.insurerName === "string"
                      ? travelInsuranceInfo.insurerName
                      : ""
                  }
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Insurer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Insurer Website
                </label>
                <input
                  type="url"
                  name="categorySpecificInfo.luggage.travelInsurance.website"
                  value={
                    typeof travelInsuranceInfo.website === "string"
                      ? travelInsuranceInfo.website
                      : ""
                  }
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="https://your-insurer.example.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Emergency Contact Number
                </label>
                <input
                  type="tel"
                  name="categorySpecificInfo.luggage.travelInsurance.emergencyContactNumber"
                  value={
                    typeof travelInsuranceInfo.emergencyContactNumber ===
                    "string"
                      ? travelInsuranceInfo.emergencyContactNumber
                      : ""
                  }
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="+61 123 456 789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Policy Number
                </label>
                <input
                  type="text"
                  name="categorySpecificInfo.luggage.travelInsurance.policyNumber"
                  value={
                    typeof travelInsuranceInfo.policyNumber === "string"
                      ? travelInsuranceInfo.policyNumber
                      : ""
                  }
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Policy #"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Notes */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Additional Notes
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notes
          </label>
          <textarea
            name="note"
            value={syncedFormData.note || ""}
            onChange={handleChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="Any additional information about your luggage or travel"
          />
        </div>
      </div>

      {/* Save Button */}
      <button
        type="submit"
        disabled={isSubmitting || toggleLoading}
        className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
      >
        {isSubmitting || toggleLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span>Saving...</span>
          </div>
        ) : (
          "Save Luggage Details"
        )}
      </button>

      {toggleError && (
        <p className="mt-4 text-red-600">Toggle Error: {toggleError}</p>
      )}
    </form>
  );
};

export default LuggageInfo;
